import React, { useMemo, useRef, useState } from 'react'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { GetImageMetdataLink } from '@makyoapp/makyo-sdk'
import { ImageMetadata } from '@makyoapp/makyo-sdk/models/Files'
import { Close8, Plus16 } from '@makyoapp/makyo-icon/icon'

import { useAppBrandKitContext } from '@/context/AppBrandKitContext'

import { useReqError } from '../../hooks/useReqError'
import { ScrollMenu, ScrollMenuItem } from '../ScrollMenu'

export function BrandKitLogoV2({ withPadding = true }: { withPadding?: boolean }) {
  const { activeBrandKit, uploadLogoBrandkit, deleteLogoBrandkit } = useAppBrandKitContext()
  const printAPIError = useReqError()

  const [file, setFile] = useState<{
    file?: File
    src?: string
  }>({})
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({})

  const imgRef = useRef(null)

  const logos = useMemo(() => {
    return activeBrandKit?.logos ? [...activeBrandKit.logos, '#src#', '#add#'] : ['#src#', '#add#']
  }, [activeBrandKit?.logos])

  const [isLoading, setIsLoading] = useBoolean(false)

  const handleImageLoadError = (e: React.SyntheticEvent<HTMLImageElement, Event>, logoUrl: string) => {
    const imgElement = e.target as HTMLImageElement

    setLoadingImages((prev) => ({ ...prev, [logoUrl]: true }))

    setTimeout(() => {
      imgElement.src = logoUrl // Retry loading the image after a short delay
    }, 1000)
  }

  return (
    <div className='w-full overflow-hidden'>
      <ScrollMenu className={clsx('flex-c py-4', logos.length > 0 && withPadding && 'px-8 py-4')}>
        {logos.map((logo, i) => {
          if (logo === '#add#') {
            return (
              <ScrollMenuItem key={i} itemId={`${i}`}>
                <div className='w-[100px]'>
                  <label
                    className={clsx(
                      'mk-bk-logo-item cursor-pointer gap-2',
                      isLoading && 'disabled opacity-10 cursor-block'
                    )}
                  >
                    <Plus16 />
                    <input
                      type='file'
                      className='sr-only'
                      accept='.jpeg,.jpg,.png,.webm'
                      disabled={isLoading}
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file && activeBrandKit) {
                          const split = file.name.split('.')
                          const ext = split[split.length - 1]
                          setIsLoading(true)
                          setFile({
                            file,
                            src: URL.createObjectURL(file),
                          })
                          uploadLogoBrandkit(activeBrandKit.id, file, ext)
                            .catch(printAPIError)
                            .finally(() => {
                              setIsLoading(false)
                              setFile({})
                            })
                        }
                      }}
                    />
                  </label>
                </div>
              </ScrollMenuItem>
            )
          } else if (logo === '#src#') {
            return file.src ? (
              <ScrollMenuItem key={i} itemId={`${i}`}>
                <div className='mk-bk-logo-item relative'>
                  {isLoading && (
                    <div className='absolute inset-0 flex-cc bg-[rgba(0,0,0,0.2)]'>
                      <span className='loading spin' />
                    </div>
                  )}
                  <img ref={imgRef} src={file.src} onLoad={() => file.src && URL.revokeObjectURL(file.src)} />
                </div>
              </ScrollMenuItem>
            ) : (
              <ScrollMenuItem key={i} itemId={`${i}`}></ScrollMenuItem>
            )
          } else {
            const link = GetImageMetdataLink(logo, ImageMetadata.XS)
            return (
              <ScrollMenuItem key={i} itemId={`${i}`}>
                <div className={clsx('mk-bk-logo-item relative', loadingImages[link] && 'animate-pulse')}>
                  <img
                    className={clsx('w-full h-full object-cover', loadingImages[link] && 'opacity-0')}
                    src={link}
                    onError={(e) => handleImageLoadError(e, link)}
                    onLoad={() => {
                      setLoadingImages((prev) => ({ ...prev, [link]: false }))
                    }}
                  />
                  <button
                    id={`logo-delete-${i}`}
                    className='mk-bk-logo-delete'
                    onClick={() => {
                      if (activeBrandKit) {
                        deleteLogoBrandkit(activeBrandKit.id, { _File: logo }).catch(printAPIError)
                      }
                    }}
                  >
                    <Close8 />
                  </button>
                </div>
              </ScrollMenuItem>
            )
          }
        })}
      </ScrollMenu>
    </div>
  )
}
